/**
 * Comportamentos da primeira carga:
 * - Se o quadro mais recente não pôde ser baixado (404/500), recua para o
 *   anterior — o mapa não fica preto esperando uma imagem que não vem.
 * - O portal ABRE PARADO NA MEDIÇÃO MAIS RECENTE (decisão de 23/09/2026, a
 *   pedido do usuário; antes abria tocando). Só o quadro na tela é baixado;
 *   o resto do loop baixa quando o usuário aperta play (`loopArmed`, ver
 *   playback.ts e preloader.ts). Por isso aqui não há mais início automático:
 *   quem abre vê o dado mais novo na hora, sem gastar a rede com 18 quadros
 *   que talvez nem assista.
 * - EXCEÇÃO: se o usuário deixou o player tocando (recarregou a página, fechou
 *   a aba tocando), a animação é retomada assim que o primeiro quadro aparece
 *   — ele só quis atualizar, não parar. Um link com quadro específico manda:
 *   abre parado nele.
 */
import { subscribeSelector } from '../lib/store';
import { useTimeStore } from '../state/time';
import { activePlanLayers, usePlanStore } from './plan';
import { hasUserInteracted, play, wasPlaying } from './playback';
import { useLoopStore } from './store';

/** Recuos no máximo (evita andar para trás indefinidamente se tudo falhar). */
const MAX_FALLBACK_STEPS = 6;

export function startStartupBehaviors(): () => void {
  let fallbackSteps = 0;
  // Lido antes de o controlador consumir o quadro pedido pelo link.
  let resume = wasPlaying() && useLoopStore.getState().pendingFrameTime === null;

  const onReady = () => {
    if (!resume) return;
    const { firstFrameShown } = usePlanStore.getState();
    if (!firstFrameShown || useTimeStore.getState().frames.length < 2) return;
    const loop = useLoopStore.getState();
    resume = false;
    if (hasUserInteracted() || loop.playing || loop.exportLocked) return;
    play();
  };

  const onStatus = () => {
    const { plan, frameStatus, firstFrameShown } = usePlanStore.getState();
    if (firstFrameShown || hasUserInteracted() || !plan || fallbackSteps >= MAX_FALLBACK_STEPS) return;
    const base = activePlanLayers(plan)[0];
    if (!base || frameStatus[base.uid] !== 'failed') return;
    const { index, setIndex } = useTimeStore.getState();
    if (index <= 0) return;
    fallbackSteps++;
    queueMicrotask(() => setIndex(index - 1));
  };

  const unsubs = [
    subscribeSelector(usePlanStore, (s) => s.frameStatus, onStatus),
    subscribeSelector(usePlanStore, (s) => s.firstFrameShown, onReady),
    subscribeSelector(useTimeStore, (s) => s.frames.length, onReady),
  ];
  onReady();
  return () => unsubs.forEach((u) => u());
}
